import type { ClinicalEpisode, ClinicalEvent } from './types';
export interface OfflineEpisodeContext {
  pregnancy: { id: string; edd: string | null; edd_method?: string | null };
  encounters: {
    patient_id?: string | null;
    allowed_subject_ids?: string[];
    id: string;
    encounter_date: string;
    encounter_type?: string | null;
    pregnancy_id?: string | null;
  }[];
  postpartum_course_tasks?: import('./postpartumCourseApi').CourseTask[];
  timeZone: string;
  patientLabel?: string;
}
export interface OfflineAuthorization {
  organizationId: string;
  userId: string;
  checkedAt: string;
  expiresAt?: string;
  permissions: string[];
}
export interface EpisodeSnapshot {
  timerActions?: import('./BirthMonitoring').BirthTimerAction[];
  episode: ClinicalEpisode;
  events: ClinicalEvent[];
  cachedAt: string;
  feedCursor?: number;
  clock?: import('./offlineClock').ClockSample;
  context?: OfflineEpisodeContext;
  authorization?: OfflineAuthorization;
}
export function mayCreateLocalProposal(
  snapshot: EpisodeSnapshot,
  organizationId: string,
  userId: string,
  now = Date.now(),
) {
  const auth = snapshot.authorization;
  return (
    !snapshot.episode.administration?.closed &&
    !!auth &&
    !!auth.expiresAt &&
    Number.isFinite(Date.parse(auth.expiresAt)) &&
    now < Date.parse(auth.expiresAt) &&
    auth.organizationId === organizationId &&
    auth.userId === userId &&
    auth.permissions.includes('read_flowsheet') &&
    (auth.permissions.includes('create_clinical_event') ||
      auth.permissions.includes('create_supervised_clinical_event'))
  );
}
/** Checks current server identity and permission before provisioning, never invents an offline server token. */
export async function checkOfflineProvisioning(
  organizationId: string,
  userId: string,
  token: string | null,
  request: typeof fetch = fetch,
): Promise<OfflineAuthorization> {
  if (!token) throw new Error('Sign in while connected before preparing offline episodes.');
  const prefix = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
  const options = {
    headers: { Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(20000),
    cache: 'no-store' as const,
  };
  const [identity, permission, configuration] = await Promise.all([
    request(`${prefix}/api/v1/auth/me`, options),
    request(`${prefix}/api/v1/auth/me/permissions?org_id=${encodeURIComponent(organizationId)}`, options),
    request(`${prefix}/api/v1/organizations/${encodeURIComponent(organizationId)}/flowsheets/configuration`, options),
  ]);
  if (!identity.ok || !permission.ok || !configuration.ok)
    throw new Error('Offline preparation requires a current authorized session.');
  const actor = (await identity.json()) as { id: string };
  const permissions = (await permission.json()) as string[];
  if (actor.id !== userId || !Array.isArray(permissions) || !permissions.includes('read_flowsheet'))
    throw new Error('Offline preparation is not authorized for this identity.');
  const config = (await configuration.json()) as { offline_authorization_hours?: number };
  const hours = config.offline_authorization_hours ?? 24;
  if (!Number.isInteger(hours) || hours < 1 || hours > 168)
    throw new Error('Offline authorization duration is invalid.');
  const now = Date.now();
  return {
    organizationId,
    userId,
    checkedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + hours * 3600000).toISOString(),
    permissions,
  };
}
